import React, { useEffect, useRef, useCallback, useMemo, useState } from "react"
import { Sankey, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts"

type SankeyNodeT = { name: string; state: string; fill: string; id: string }
type SankeyLinkT = {
  source: number
  target: number
  value: number
  color?: string
  __fromState?: string
  __toState?: string
  average?: number
  id: string
  sourceName?: string
  targetName?: string
  sourceState?: string
  targetState?: string
}
type SankeyGraphT = { nodes: SankeyNodeT[]; links: SankeyLinkT[] }

type LegendNode = { index: number; name: string; color: string; centerX: number }

interface ApplicationSankeyDiagramProps {
  stateTransitionStats: Record<
    string,
    {
      average: number
      minimum: number
      maximum: number
      totalCount: number
      totalDuration: number
    }
  >
  width?: number
  height?: number
}

const ApplicationSankeyDiagram: React.FC<ApplicationSankeyDiagramProps> = ({
  stateTransitionStats,
}) => {
  const [sankeyTopN, setSankeyTopN] = useState<number>(12)
  const [showOptionalTransitions, setShowOptionalTransitions] = useState<boolean>(false)
  const legendMapRef = useRef<Map<number, LegendNode>>(new Map())
  const legendTimer = useRef<NodeJS.Timeout | null>(null)
  const [legendList, setLegendList] = useState<LegendNode[]>([])
  const [hoverNode, setHoverNode] = useState<number | null>(null)
  const [hoverLink, setHoverLink] = useState<number | null>(null)

  const getStateDisplayName = (state: string) => {
    const stateNames: { [key: string]: string } = {
      invite_new: "Yeni Davet",
      invite_success: "Davet Başarılı",
      first_application: "İlk Başvuru",
      waiting_address: "Adres Bekliyor",
      address_to_approve: "Adres Onay Bekliyor",
      address_rejected: "Adres Reddedildi",
      waiting_content: "İçerik Bekliyor",
      content_to_share: "Paylaşılacak İçerik",
      content_approved: "İçerik Onaylandı",
      content_rejected: "İçerik Reddedildi",
      content_shared: "İçerik Paylaşıldı",
      ugc_content: "UGC İçerik",
      ugc_rejected: "UGC Reddedildi",
      application_done: "Başvuru Tamamlandı",
      account_rejected: "Hesap Reddedildi",
      user_canceled: "Kullanıcı İptal",
      brand_canceled: "Marka İptal",
    }
    return stateNames[state] || state
  }

  const getStateColor = (state: string) => {
    const colors: { [key: string]: string } = {
      invite_new: "#F59E0B", // Orange
      invite_success: "#10B981", // Green
      first_application: "#3B82F6", // Blue
      waiting_address: "#EAB308", // Yellow
      address_to_approve: "#F97316", // Orange
      address_rejected: "#EF4444", // Red
      waiting_content: "#8B5CF6", // Purple
      content_to_share: "#6366F1", // Indigo
      content_approved: "#059669", // Emerald
      content_rejected: "#DC2626", // Red
      content_shared: "#0EA5E9", // Sky blue
      ugc_content: "#16A34A", // Green
      ugc_rejected: "#B91C1C", // Red
      application_done: "#047857", // Emerald
      account_rejected: "#991B1B", // Red
      user_canceled: "#7C3AED", // Purple
      brand_canceled: "#BE185D", // Pink
    }
    return colors[state] || "#6B7280"
  }

  const reportNodeLayout = useCallback((entry: LegendNode) => {
    legendMapRef.current.set(entry.index, entry)
    if (legendTimer.current) {
      clearTimeout(legendTimer.current)
    }
    legendTimer.current = setTimeout(() => {
      const arr = Array.from(legendMapRef.current.values()).sort((a, b) => a.centerX - b.centerX)
      setLegendList(arr)
    }, 0)
  }, [])

  const buildSankeyFromTransitions = (
    transitions: { from: string; to: string; count: number; average: number }[],
  ): SankeyGraphT => {
    const sortedTransitions = [...transitions].sort((a, b) => b.count - a.count)

    const mainWorkflow = [
      "first_application",
      "waiting_content",
      "content_to_share",
      "content_approved",
      "content_shared",
      "application_done",
    ]

    const optionalTransitions = [
      { from: "first_application", to: "account_rejected" },
      { from: "first_application", to: "user_canceled" },
    ]

    const finalTransitions: typeof sortedTransitions = []
    const processedPairs = new Set<string>()

    for (let i = 0; i < mainWorkflow.length - 1; i++) {
      const from = mainWorkflow[i]
      const to = mainWorkflow[i + 1]

      const mainTransition = sortedTransitions.find((t) => t.from === from && t.to === to)
      if (mainTransition) {
        finalTransitions.push(mainTransition)
        processedPairs.add(`${from}->${to}`)
      }
    }

    if (showOptionalTransitions) {
      optionalTransitions.forEach(({ from, to }) => {
        const optionalTransition = sortedTransitions.find((t) => t.from === from && t.to === to)
        if (optionalTransition) {
          finalTransitions.push(optionalTransition)
          processedPairs.add(`${from}->${to}`)
        }
      })
    }

    for (const transition of sortedTransitions) {
      const pairKey = `${transition.from}->${transition.to}`
      const reversePairKey = `${transition.to}->${transition.from}`

      if (processedPairs.has(pairKey)) {
        continue
      }

      if (processedPairs.has(reversePairKey)) {
        continue
      }

      if (!showOptionalTransitions) {
        const isOptionalTransition = optionalTransitions.some(
          (opt) => opt.from === transition.from && opt.to === transition.to,
        )
        if (isOptionalTransition) {
          continue
        }
      }

      processedPairs.add(pairKey)
      finalTransitions.push(transition)
    }

    const merged = finalTransitions

    const nodeStates = new Set<string>()
    merged.forEach((t) => {
      nodeStates.add(t.from)
      nodeStates.add(t.to)
    })

    const nodes: SankeyNodeT[] = Array.from(nodeStates).map((state, index) => ({
      name: getStateDisplayName(state),
      state,
      fill: getStateColor(state),
      id: `node-${index}`,
    }))

    const idx = new Map(nodes.map((n, i) => [n.state, i]))

    const links: SankeyLinkT[] = merged.flatMap((t) => {
      const sIdx = idx.get(t.from)
      const tIdx = idx.get(t.to)
      if (sIdx == null || tIdx == null || sIdx === tIdx) {
        return []
      }
      return [
        {
          source: sIdx,
          target: tIdx,
          value: t.count,
          color: getStateColor(t.from),
          __fromState: t.from,
          __toState: t.to,
          average: t.average,
          id: `link-${sIdx}-${tIdx}`,
          sourceName: getStateDisplayName(t.from),
          targetName: getStateDisplayName(t.to),
          sourceState: t.from,
          targetState: t.to,
        },
      ]
    })

    return { nodes, links }
  }

  const sankeyData = useMemo<SankeyGraphT>(() => {
    const transitions: { from: string; to: string; count: number; average: number }[] = []

    Object.entries(stateTransitionStats).forEach(([transition, stats]) => {
      const [from, to] = transition.split("_TO_")

      if (
        from &&
        to &&
        typeof stats.totalCount === "number" &&
        !isNaN(stats.totalCount) &&
        stats.totalCount > 0 &&
        typeof stats.average === "number" &&
        !isNaN(stats.average) &&
        stats.average >= 0 &&
        from !== to
      ) {
        transitions.push({
          from,
          to,
          count: stats.totalCount,
          average: stats.average,
        })
      }
    })

    return buildSankeyFromTransitions(transitions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateTransitionStats, sankeyTopN, showOptionalTransitions])

  const getSourceNodeIndex = (lp: {
    source: { index: number }
    link: { source: { index: number } }
    index: number
  }): number | undefined => {
    if (lp?.source && typeof lp.source === "object" && typeof lp.source.index === "number") {
      return lp.source.index
    }
    if (lp?.link?.source && typeof lp.link.source.index === "number") {
      return lp.link.source.index
    }
    if (typeof lp?.index === "number" && sankeyData?.links?.[lp.index]) {
      const src = (sankeyData.links[lp.index] as SankeyLinkT).source
      if (typeof src === "number") {
        return src
      }
    }
    return undefined
  }

  const PrettySankeyNode: React.FC<{
    x: number
    y: number
    width: number
    height: number
    payload: { fill: string; name: string }
    index: number
    onMouseEnter: () => void
    onMouseLeave: () => void
    reportLayout: (entry: LegendNode) => void
  }> = (props) => {
    const { x, y, width, height, payload, index, onMouseEnter, onMouseLeave, reportLayout } = props
    const color = payload.fill || "#9CA3AF"

    useEffect(() => {
      if (!reportLayout) {
        return
      }
      const cx = Number(x) + Number(width) / 2
      reportLayout({
        index,
        name: payload?.name ?? "",
        color,
        centerX: cx,
      })
    }, [index, x, width, payload?.name, color, reportLayout])

    return (
      <g
        key={`node-group-${index}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ cursor: "pointer" }}
      >
        <defs key={`defs-${index}`}>
          <filter id={`sankey-shadow-${index}`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodOpacity="0.25" />
          </filter>
        </defs>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={0}
          ry={0}
          fill={color}
          filter={`url(#sankey-shadow-${index})`}
          stroke="rgba(0,0,0,.12)"
          strokeWidth={0.5}
        />
      </g>
    )
  }

  const buildThickPath = ({
    sourceX,
    sourceY,
    targetX,
    targetY,
    linkWidth,
  }: {
    sourceX: number
    sourceY: number
    targetX: number
    targetY: number
    linkWidth: number
  }) => {
    const half = Math.max(1, (linkWidth ?? 1) / 2)
    const xi = (sourceX + targetX) / 2
    return [
      `M ${sourceX},${sourceY - half}`,
      `C ${xi},${sourceY - half} ${xi},${targetY - half} ${targetX},${targetY - half}`,
      `L ${targetX},${targetY + half}`,
      `C ${xi},${targetY + half} ${xi},${sourceY + half} ${sourceX},${sourceY + half}`,
      "Z",
    ].join(" ")
  }

  const nf = new Intl.NumberFormat("tr-TR")

  const PrettySankeyLink: React.FC<any> = (props) => {
    const {
      onClick,
      index,
      value,
      onMouseEnter,
      onMouseLeave,
      isDimmed,
      path,
      d,
      link,
      sourceX,
      sourceY,
      targetX,
      targetY,
      linkWidth,
    } = props

    const colorFromData = (sankeyData?.links?.[index] as SankeyLinkT)?.color as string | undefined

    const srcIdx = getSourceNodeIndex(props)
    const colorFromNode =
      (srcIdx != null && (sankeyData?.nodes?.[srcIdx] as SankeyNodeT)?.fill) ||
      (srcIdx != null && (sankeyData?.nodes?.[srcIdx] as SankeyNodeT)?.state
        ? getStateColor((sankeyData?.nodes?.[srcIdx] as SankeyNodeT).state)
        : undefined)

    const sColor = colorFromData || colorFromNode || "#94A3B8"

    const pathD =
      path ||
      d ||
      (link && (link.path || link.d)) ||
      buildThickPath({ sourceX, sourceY, targetX, targetY, linkWidth })

    const cx = (Number(sourceX) + Number(targetX)) / 2
    const cy = (Number(sourceY) + Number(targetY)) / 2

    const valueText = nf.format(value ?? 0)
    const showLabel = (value ?? 0) >= 3
    const labelW = Math.max(36, valueText.length * 8 + 12)

    return (
      <g
        key={`link-group-${index}`}
        opacity={isDimmed ? 0.18 : 0.95}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        style={{ cursor: "pointer", pointerEvents: "all" }}
      >
        <path key={`path-fill-${index}`} d={pathD} fill={sColor} style={{ fill: sColor }} />
        <path
          key={`path-stroke-${index}`}
          d={pathD}
          fill="none"
          stroke="rgba(0,0,0,.10)"
          strokeWidth={0.6}
        />

        {showLabel && (
          <React.Fragment key={`label-${index}`}>
            <rect
              key={`rect-${index}`}
              x={cx - labelW / 2}
              y={cy - 10}
              width={labelW}
              height={20}
              rx={10}
              fill="rgba(255,255,255,.95)"
              stroke="rgba(15,23,42,.08)"
            />
            <text
              key={`text-${index}`}
              x={cx}
              y={cy}
              dominantBaseline="middle"
              textAnchor="middle"
              fontSize={11}
              fill="#0f172a"
            >
              {valueText}
            </text>
          </React.Fragment>
        )}
      </g>
    )
  }

  const SankeyTooltipContent: React.FC<any> = ({ active, payload }) => {
    if (!active || !payload?.length) {
      return null
    }

    const linkData = payload[0].payload

    const actualLinkData = hoverLink !== null ? (sankeyData.links[hoverLink] as SankeyLinkT) : null

    const sourceName =
      actualLinkData?.sourceName || linkData.source?.name || linkData.sourceName || ""
    const targetName =
      actualLinkData?.targetName || linkData.target?.name || linkData.targetName || ""
    const sourceState =
      actualLinkData?.sourceState || linkData.source?.state || linkData.sourceState || ""
    const targetState =
      actualLinkData?.targetState || linkData.target?.state || linkData.targetState || ""
    const value = linkData.value || 0
    const average = actualLinkData?.average || linkData.average || 0

    const averageHours = Math.round(average / (1000 * 60 * 60))
    const averageDays = Math.round(averageHours / 24)

    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-lg">
        <div className="font-medium text-slate-800 mb-2">
          <span className="text-blue-600">{sourceName}</span>
          <span className="mx-2">→</span>
          <span className="text-green-600">{targetName}</span>
        </div>
        <div className="text-xs text-gray-500 mb-2">
          <span className="font-mono">{sourceState}</span>
          <span className="mx-2">→</span>
          <span className="font-mono">{targetState}</span>
        </div>
        <div className="space-y-1 text-slate-700">
          <div>
            <span className="font-medium">Başvuru Sayısı:</span>
            <span className="ml-1 font-bold text-blue-600">
              {new Intl.NumberFormat("tr-TR").format(value)}
            </span>
          </div>
          <div>
            <span className="font-medium">Ortalama Süre:</span>
            <span className="ml-1 font-bold text-green-600">
              {averageDays > 0 ? `${averageDays} gün` : `${averageHours} saat`}
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (!stateTransitionStats || Object.keys(stateTransitionStats).length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-gray-400 text-4xl mb-2">📊</div>
          <p className="text-gray-500">Sankey diyagramı için veri bulunamadı</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Başvuru Durumu Geçişleri</h3>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-3">
            <label className="text-gray-600">Gösterilecek akış:</label>
            <input
              type="number"
              min={5}
              max={30}
              value={sankeyTopN}
              onChange={(e) => setSankeyTopN(Number(e.target.value || 12))}
              className="w-16 border rounded px-2 py-1"
              title="En güçlü kaç akış gösterilsin?"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showOptionalTransitions"
              checked={showOptionalTransitions}
              onChange={(e) => setShowOptionalTransitions(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="showOptionalTransitions" className="text-gray-600 cursor-pointer">
              Red/İptal geçişlerini göster
            </label>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="h-[420px]">
          <ResponsiveContainer width="100%" height="100%">
            <Sankey
              data={sankeyData}
              nodeWidth={18}
              nodePadding={28}
              linkCurvature={0.5}
              iterations={32}
              node={(nodeProps: any) => (
                <PrettySankeyNode
                  key={`node-${nodeProps.index}`}
                  {...nodeProps}
                  reportLayout={reportNodeLayout}
                  onMouseEnter={() => setHoverNode(nodeProps.index)}
                  onMouseLeave={() => setHoverNode(null)}
                />
              )}
              link={(linkProps: any) => {
                const linkData = sankeyData.links[linkProps.index] as any

                const srcIdx =
                  typeof linkProps?.source?.index === "number"
                    ? linkProps.source.index
                    : typeof linkData?.source === "number"
                    ? linkData.source
                    : linkData?.source?.index

                const colorFromData = (sankeyData.links[linkProps.index] as SankeyLinkT)?.color
                const colorFromNode =
                  typeof srcIdx === "number"
                    ? (sankeyData.nodes[srcIdx] as SankeyNodeT)?.fill
                    : undefined
                const sColor = colorFromData || colorFromNode || "#94A3B8"

                const connectedToHoverNode =
                  hoverNode !== null &&
                  (linkProps.source?.index === hoverNode || linkProps.target?.index === hoverNode)

                const isDimmed =
                  hoverLink !== null
                    ? linkProps.index !== hoverLink
                    : hoverNode !== null
                    ? !connectedToHoverNode
                    : false

                return (
                  <PrettySankeyLink
                    key={`link-${linkProps.index}`}
                    {...linkProps}
                    isDimmed={isDimmed}
                    onMouseEnter={() => setHoverLink(linkProps.index)}
                    onMouseLeave={() => setHoverLink(null)}
                    style={{ fill: sColor }}
                  />
                )
              }}
            >
              <RechartsTooltip content={<SankeyTooltipContent data={sankeyData} />} />
            </Sankey>
          </ResponsiveContainer>
        </div>
        {legendList.length > 0 && (
          <div className="mt-3 -mx-4 px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
              {legendList.map((n) => (
                <div
                  key={n.index}
                  className="flex items-center gap-2 px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700 text-xs shadow-sm"
                  title={n.name}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ background: n.color }}
                  />
                  <span className="truncate max-w-[200px] sm:max-w-[260px]">{n.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>💡 Geçiş çizgilerinin üzerine gelerek detayları görebilirsiniz.</p>
      </div>
    </div>
  )
}

export default ApplicationSankeyDiagram
